import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Pagination,
  TablePagination,
  Tooltip,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import useTouchInteraction from "../../hooks/useTouchInteraction";

const TransactionDetailsDialog = ({ transaction, onClose, isMobile }) => {
  // Move hooks outside conditional
  const { handlers: closeHandlers, touchStates: closeTouchStates } =
    useTouchInteraction(onClose, true);
  const {
    handlers: bottomButtonHandlers,
    touchStates: bottomButtonTouchStates,
  } = useTouchInteraction(onClose, true);

  if (!transaction) return null;

  const detailFields = [
    { label: "Transaction Type", value: transaction.transaction_type },
    {
      label: "Amount",
      value: `₹ ${Math.abs(transaction.amount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`,
    },
    { label: "Status", value: transaction.status },
    { label: "Transaction ID", value: transaction.transaction_id},
    { label: "Date", value: new Date(transaction.created_at).toLocaleString() },
  ];

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: isMobile ? "80vh" : "400px",
          m: isMobile ? 0 : 2,
          position: "relative",
          borderRadius: isMobile ? "16px 16px 0 0" : 2,
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: isMobile ? "flex-end" : "center",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          borderBottom: "3px solid",
          borderColor: transaction.amount > 0 ? "success.main" : "error.main",
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {transaction.transaction_type}
          </Typography>
          <Typography
            sx={{
              color: transaction.amount > 0 ? "success.main" : "error.main",
              fontWeight: "bold",
            }}
          >
            ₹ {Math.abs(transaction.amount).toLocaleString()}
          </Typography>
        </Box>
        <Box
          {...closeHandlers}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor:
              closeTouchStates.isActive && !closeTouchStates.isTouchMoved
                ? "action.hover"
                : "transparent",
            transition: "background-color 0.2s ease",
            WebkitTapHighlightColor: "transparent",
            "&:active": {
              backgroundColor: !closeTouchStates.isTouchMoved
                ? "action.hover"
                : "transparent",
            },
          }}
        >
          {/* <CloseIcon sx={{ 
            color: 'text.secondary',
            pointerEvents: 'none'
          }} /> */}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          mt: 2,
          px: isMobile ? 2 : 3,
          py: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: 2,
            pb: 2,
          }}
        >
          {detailFields.map((field, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.default",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {field.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>
        {transaction.rejection_message && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 1,
              backgroundColor: "#e1f5fe",
              border: "1px solid",
              borderColor: "#b3e5fc",
            }}
          >
            <Typography variant="body2" color="text.primary">
              Rejection Reason: {transaction.rejection_message}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {isMobile && (
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            position: "sticky",
            bottom: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
          }}
        >
          <Button
            {...bottomButtonHandlers}
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              backgroundColor:
                bottomButtonTouchStates.isActive &&
                !bottomButtonTouchStates.isTouchMoved
                  ? "action.hover"
                  : "primary.main",
              transition: "background-color 0.2s ease",
              WebkitTapHighlightColor: "transparent",
              "&:active": {
                backgroundColor: !bottomButtonTouchStates.isTouchMoved
                  ? "action.hover"
                  : "primary.main",
              },
            }}
          >
            Close
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

const TransactionCard = React.memo(({ transaction, onSelect }) => {
  const theme = useTheme();

  const handleTap = useCallback(() => {
    onSelect(transaction);
  }, [transaction, onSelect]);

  const { handlers, touchStates } = useTouchInteraction(handleTap, true);

  return (
    <Paper
      {...handlers}
      elevation={1}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        backgroundColor:
          touchStates.isActive && !touchStates.isTouchMoved
            ? "action.hover"
            : "background.paper",
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        transition: "background-color 0.2s ease",
        "&:active": {
          backgroundColor: !touchStates.isTouchMoved
            ? "action.hover"
            : "background.paper",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {new Date(transaction.created_at).toLocaleString()}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color:
              transaction.status?.toUpperCase() === "CANCELLED" || transaction.status?.toUpperCase() === "FAILED"
                ? "error.main"
                : "text.primary",
            fontWeight: transaction.status?.toUpperCase() === "CANCELLED" ? "bold" : "normal",
            textTransform: "uppercase",
          }}
        >
          {transaction.status}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
          {transaction.transaction_type}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: transaction.amount > 0 ? "success.main" : "error.main",
            fontWeight: "bold",
          }}
        >
          ₹ {Math.abs(transaction.amount).toLocaleString()}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Transaction ID
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: "medium" }}>
          {transaction.merchant_transaction_id}
        </Typography>
      </Box>
    </Paper>
  );
});

const TransactionView = ({ payoutTransactions, pageNo, setPageNo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPageNo(newPage + 1); // Add 1 since pageNo is 1-based
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageNo(1); // Reset to first page when changing rows per page
  };

  // Add touch handling to prevent parent scroll
  const handleTouchMove = useCallback((event) => {
    const element = event.currentTarget;
    const isAtTop = element.scrollTop === 0;
    const isAtBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    if (
      (isAtTop && event.touches[0].clientY > 0) ||
      (isAtBottom && event.touches[0].clientY < 0)
    ) {
      event.preventDefault();
    }
  }, []);

  if (payoutTransactions.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          py: 6,
          textAlign: "center",
        }}
      >
        <ReceiptLongIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
          No Transactions
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You have no transaction history at the moment.
        </Typography>
      </Box>
    );
  }

  // Adjust for 1-based pageNo
  const currentPageData = payoutTransactions.slice(
    (pageNo - 1) * rowsPerPage,
    (pageNo - 1) * rowsPerPage + rowsPerPage
  );

  return (
    <>
      {isMobile ? (
        <Box
          onTouchMove={handleTouchMove}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: 1,
            pb: 2,
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "none",
            touchAction: "pan-y",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {currentPageData.map((transaction, index) => (
            <TransactionCard
              key={index}
              transaction={transaction}
              onSelect={setSelectedTransaction}
            />
          ))}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              pb: 7, // Add padding to account for bottom navigation
            }}
          >
            <Pagination
              count={Math.ceil(payoutTransactions.length / rowsPerPage)}
              page={pageNo}
              onChange={(e, value) => setPageNo(value)}
              color="primary"
              size="small"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontSize: "0.875rem",
                  minWidth: 32,
                  height: 32,
                  borderRadius: 1,
                },
                "& .Mui-selected": {
                  fontWeight: "bold",
                },
              }}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            height: "100%",
          }}
        >
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto', // Enable vertical scroll
              backgroundColor: theme.palette.background.default,
              "& .MuiTableCell-root": {
                px: 1.5,
                py: 1,
                fontSize: "0.875rem",
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            <Table
              size="medium"
              sx={{
                tableLayout: "fixed",
                minWidth: 900,
              }}
            >
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "white",
                  zIndex: 1,
                }}
              >
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ width: "15%" }}>Date</TableCell>
                  <TableCell sx={{ width: "15%" }}>Type</TableCell>
                  <TableCell sx={{ width: "15%" }}>Amount</TableCell>
                  <TableCell sx={{ width: "20%" }}>Transaction ID</TableCell>
                  <TableCell sx={{ width: "35%" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payoutTransactions.map((transaction, index) => (
                  <TableRow
                    key={index}
                    onClick={() => setSelectedTransaction(transaction)}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                        cursor: "pointer",
                      },
                    }}
                  >
                    <TableCell>
                      {new Date(transaction.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{transaction.transaction_type}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            transaction.amount > 0
                              ? "success.main"
                              : "error.main",
                          fontWeight: "bold",
                        }}
                      >
                        ₹ {Math.abs(transaction.amount).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.transaction_id}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            transaction.status?.toUpperCase() === "CANCELLED" || transaction.status?.toUpperCase() === "FAILED"
                              ? "error.main"
                              : "text.primary",
                          fontWeight:
                            transaction.status?.toUpperCase() === "CANCELLED" || transaction.status?.toUpperCase() === "FAILED"
                              ? "bold"
                              : "normal",
                          textTransform: "uppercase",
                        }}
                      >
                        {transaction.status}
                      </Typography>
                      {transaction.rejection_message && (
                        <Tooltip title={`Rejection Reason: ${transaction.rejection_message}`}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              mt: 0.5,
                            }}
                          >
                            Rejection Reason: {transaction.rejection_message}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {selectedTransaction && (
        <TransactionDetailsDialog
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          isMobile={isMobile}
        />
      )}
    </>
  );
};

export default TransactionView;
